// Home Insights helpers — extracted from App.js for maintainability
// Phase detection, weekly notes, streak milestones

export const PHASES = {
  pre: 'Pre-Surgery Prep',
  clear: 'Clear Liquids',
  full: 'Full Liquids',
  pureed: 'Pureed Foods',
  soft: 'Soft Foods',
  regular: 'Regular Foods',
};

export const getPhaseInfo = (surgeryDate) => {
  if (!surgeryDate) return { label: PHASES.pre, weeksSince: 0, phaseKey: 'pre' };
  const start = new Date(surgeryDate);
  if (isNaN(start.getTime())) return { label: PHASES.pre, weeksSince: 0, phaseKey: 'pre' };
  const days = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
  const weeks = Math.max(0, Math.floor(days / 7));
  if (days < 0) return { label: PHASES.pre, weeksSince: 0, phaseKey: 'pre' };
  if (weeks < 2) return { label: PHASES.clear, weeksSince: weeks, phaseKey: 'clear' };
  if (weeks < 4) return { label: PHASES.full, weeksSince: weeks, phaseKey: 'full' };
  if (weeks < 8) return { label: PHASES.pureed, weeksSince: weeks, phaseKey: 'pureed' };
  if (weeks < 12) return { label: PHASES.soft, weeksSince: weeks, phaseKey: 'soft' };
  return { label: PHASES.regular, weeksSince: weeks, phaseKey: 'regular' };
};

export const isoWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

export const WEEKLY_NOTES = {
  pre: [
    'You are preparing to change your life. Trust the process.',
    'Prep your pantry this week — protein first, always.',
    'Small steps now become big wins after surgery.',
  ],
  clear: [
    "Sip, don't gulp. Your new stomach is healing beautifully.",
    'Every ounce counts — celebrate small hydration wins.',
    'Rest is progress. Give your body the time it needs.',
  ],
  full: [
    'Protein shakes are your new best friend — aim for 60-80g daily.',
    'Add a walk today. Even 5 minutes lifts your energy.',
    'Your body is doing hard work. Be gentle with yourself.',
  ],
  pureed: [
    'Chew (or blend) thoroughly. Texture is everything right now.',
    'Try one new pureed protein this week to keep things interesting.',
    "You're building lifelong habits, one meal at a time.",
  ],
  soft: [
    "Protein first, veggies second, carbs last — that's the winning order.",
    'Notice how your body feels full sooner. Honor that signal.',
    "You've come so far. Look back at week 1 and celebrate.",
  ],
  regular: [
    "Consistency > perfection. One meal off-plan won't undo your progress.",
    'Move your body in a way that feels joyful this week.',
    'Your journey inspires the next person just starting out.',
  ],
};

export const PREMIUM_TIPS = {
  pre: 'Book a bariatric-focused therapist consult now — mental prep matters as much as physical.',
  clear: 'Track every sip. Aim for 48-64oz clear liquids in tiny doses spaced 15 min apart.',
  full: 'If shakes upset your stomach, switch to unflavored isolate + almond milk. Denise recommends Unjury.',
  pureed: 'Batch-cook 3 pureed proteins (chicken, salmon, cottage cheese) for the week — freeze half.',
  soft: 'Introduce one new dense protein per week: eggs → fish → chicken → red meat (last).',
  regular: 'Start strength training 2x/week — muscle protects your metabolism long-term.',
};

export const getWeeklyNote = (phaseKey) => {
  const list = WEEKLY_NOTES[phaseKey] || WEEKLY_NOTES.regular;
  return list[isoWeek() % list.length];
};

// Streak milestone thresholds (days). Fires a toast when user crosses one.
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100];

/**
 * Detects if a new streak milestone was crossed since last check.
 * Returns array of { kind: 'vitamin'|'fluid', days: 7, label: '7-Day Streak!' } for any new milestone.
 * Persists last-seen values in localStorage.
 */
export const detectStreakMilestones = (userId, currentStreaks) => {
  if (!userId || !currentStreaks) return [];
  const key = `gb_streak_last_${userId}`;
  let last = { vitamin: 0, fluid: 0 };
  try {
    const raw = localStorage.getItem(key);
    if (raw) last = { ...last, ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }

  const v = currentStreaks.vitamin_streak_days || 0;
  const f = currentStreaks.fluid_streak_days || 0;
  const events = [];

  STREAK_MILESTONES.forEach((m) => {
    if (last.vitamin < m && v >= m) {
      events.push({ kind: 'vitamin', days: m, label: `${m}-Day Vitamin Streak!` });
    }
    if (last.fluid < m && f >= m) {
      events.push({ kind: 'fluid', days: m, label: `${m}-Day Fluid Streak!` });
    }
  });

  try {
    localStorage.setItem(key, JSON.stringify({ vitamin: v, fluid: f }));
  } catch (e) { /* ignore */ }
  return events;
};
